import { LAYER_TYPES } from '../project-settings/types';

export type CadTool =
  | 'draw'
  | 'pan'
  | 'select'
  | 'circle'
  | 'roundabout'
  | 'subtract'
  | 'subtract-circle'
  | 'split'
  | 'measure'
  | 'set-scale';
export type CadViewMode = '2d' | '3d';

/** Predefined architectural drawing scale ratios */
export const CAD_SCALE_RATIOS = [
  { value: '1:10', ratio: 10 },
  { value: '1:20', ratio: 20 },
  { value: '1:50', ratio: 50 },
  { value: '1:100', ratio: 100 },
  { value: '1:200', ratio: 200 },
  { value: '1:500', ratio: 500 },
  { value: '1:1000', ratio: 1000 },
  { value: '1:2000', ratio: 2000 },
  { value: '1:5000', ratio: 5000 },
  { value: '1:10000', ratio: 10000 },
] as const;

export type CadScaleRatio = (typeof CAD_SCALE_RATIOS)[number]['value'];

export interface CadPolygon {
  id: string;
  name: string;
  points: [number, number][];
  holes?: [number, number][][];
  area: number;
  perimeter: number;
  height: number;
  color: string;
  type: keyof typeof LAYER_TYPES;
  illuminated: boolean;
  canHoldLight: boolean;
  norm: string;
  desiredLuminaireHeight: number;
}

/** A single undo/redo command — stores enough info to reverse or reapply. */
export type UndoCommand =
  | { type: 'ADD_POLYGON'; polygonId: string }
  /** Many polygons at once (created from an imported drawing's layer): one undo step. */
  | { type: 'ADD_POLYGONS'; polygonIds: string[] }
  | { type: 'DELETE_POLYGONS'; polygons: CadPolygon[] }
  | { type: 'DELETE_POLYGON'; polygon: CadPolygon; index: number }
  | { type: 'UPDATE_POLYGON'; polygonId: string; oldValues: Partial<CadPolygon> }
  /** The same change to several polygons (multi-select in the sidebar): one undo step. */
  | { type: 'UPDATE_POLYGONS'; changes: { polygonId: string; oldValues: Partial<CadPolygon> }[] }
  | {
      type: 'SUBTRACT_FROM_POLYGON';
      polygonId: string;
      oldPoints: [number, number][];
      oldHoles?: [number, number][][];
      oldArea: number;
      oldPerimeter: number;
    }
  | {
      type: 'SPLIT_POLYGON';
      originalPolygon: CadPolygon;
      originalIndex: number;
      newPolygonIds: string[];
    };

export interface CadEditorState {
  polygons: CadPolygon[];
  tool: CadTool;
  viewMode: CadViewMode;
  snapEnabled: boolean;
  gridSize: number;
  /** Whether scale has been calibrated (two-point measurement or preset selected) */
  scaleSet: boolean;
  /** The two calibration points placed on the image (world coordinates) */
  scalePoints: [number, number][];
  /** Meters per coordinate unit — derived from calibration or preset ratio */
  metersPerUnit: number;
  /** Currently selected predefined scale ratio, or null for custom calibration */
  scaleRatio: CadScaleRatio | null;
  drawingPoints: [number, number][];
  activePolygonIndex: number | null;
  projectName: string;
  undoStack: UndoCommand[];
  redoStack: UndoCommand[];
  measurementPoints: [number, number][];
}
